// RevenueProjections — three-card period view (Month / Quarter / Year)
// with a forward-looking projection per period and a cumulative
// recognized-vs-projected mini-chart below the selected card.
//
// Lives under RevenueTicker in Analytics' revenue section. Reads the
// same `jobs` array Analytics already loads (no new queries) and uses
// the shared revenueData primitives so the two surfaces never drift in
// how they classify or timestamp revenue.
//
// Projection math (per approved spec):
//   dailyPace      = recognized-to-date / max(1, daysElapsed)
//   projectedDay   = max(dailyPace, scheduledTotalForThatDay)
//   projection     = recognized-to-date + Σ projectedDay over remaining days
//
// The max() ensures booked work pulls the projection up when the pace
// alone would understate the remainder of the period, while a slow week
// with no scheduled jobs doesn't collapse the projection to zero.
//
// Cumulative chart: both series accumulate by bucket so the lines RISE
// to the projected total. Recognized runs through today; projection
// continues from today's recognized-to-date through period end at the
// projected daily values. A vertical reference line at "today" marks
// the boundary.

import { useEffect, useMemo, useState } from 'react'
import {
  ComposedChart, Area, Line, ReferenceLine, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from 'recharts'
import {
  startOfMonth, endOfMonth, startOfQuarter, endOfQuarter,
  startOfYear, endOfYear, startOfDay, endOfDay,
  format, eachDayOfInterval, eachMonthOfInterval,
  isWithinInterval, differenceInCalendarDays,
} from 'date-fns'
import { formatCurrency } from '@/lib/utils'
import { completedAt, scheduledAt, isRecognized, isScheduled, jobValue } from '@/pages/analytics/revenueData'

type PeriodId = 'month' | 'quarter' | 'year'

const PERIOD_KEY = 'orkestra-projection-period'
const PERIODS: { id: PeriodId; label: string; bucketBy: 'day' | 'month' }[] = [
  { id: 'month',   label: 'This Month',   bucketBy: 'day' },
  { id: 'quarter', label: 'This Quarter', bucketBy: 'day' },
  { id: 'year',    label: 'This Year',    bucketBy: 'month' },
]

function periodWindow(id: PeriodId, now: Date): { start: Date; end: Date } {
  switch (id) {
    case 'month':   return { start: startOfMonth(now),   end: endOfMonth(now) }
    case 'quarter': return { start: startOfQuarter(now), end: endOfQuarter(now) }
    case 'year':    return { start: startOfYear(now),    end: endOfYear(now) }
  }
}

// Compute per-period stats. Recognized-to-date is straightforward; the
// projection iterates remaining buckets and applies max(pace, scheduled)
// so booked future work raises the bar even on a slow current pace.
function computeStats(id: PeriodId, jobs: any[], now: Date) {
  const { start, end } = periodWindow(id, now)
  const today = startOfDay(now)
  const completeJobs  = jobs.filter(isRecognized)
  const scheduledJobs = jobs.filter(isScheduled)

  // Recognized-to-date (everything completed inside the period up to now).
  let recognizedToDate = 0
  for (const j of completeJobs) {
    const dt = completedAt(j)
    if (!dt) continue
    if (isWithinInterval(dt, { start, end: now })) recognizedToDate += jobValue(j)
  }

  // Per-day scheduled totals for the WHOLE period — used twice: to drive
  // the projection's max(pace, scheduled) and to compose the cumulative
  // projection line on the chart. Buckets are days for month/quarter,
  // months for year (per the chart granularity).
  const days = eachDayOfInterval({ start, end })
  const scheduledByDay = new Map<string, number>()
  for (const d of days) scheduledByDay.set(format(d, 'yyyy-MM-dd'), 0)
  for (const j of scheduledJobs) {
    const dt = scheduledAt(j)
    if (!dt) continue
    if (!isWithinInterval(dt, { start, end })) continue
    const key = format(dt, 'yyyy-MM-dd')
    const prev = scheduledByDay.get(key) ?? 0
    scheduledByDay.set(key, prev + jobValue(j))
  }

  // Daily pace from days elapsed (inclusive of today). Guard min=1 so a
  // first-day-of-period view doesn't divide by zero.
  const daysElapsed = Math.max(1, differenceInCalendarDays(today, start) + 1)
  const dailyPace = recognizedToDate / daysElapsed

  // Project remaining days. Tomorrow onward — today's recognized total
  // is already in recognizedToDate, so adding pace for today would
  // double-count partial-day completions.
  let projectedRemainder = 0
  for (const d of days) {
    if (d <= today) continue
    const sch = scheduledByDay.get(format(d, 'yyyy-MM-dd')) ?? 0
    projectedRemainder += Math.max(dailyPace, sch)
  }

  const projection = recognizedToDate + projectedRemainder

  // Percent of period elapsed (1-indexed, capped at 100). Used for the
  // small tertiary sub-label on each card.
  const totalDays = differenceInCalendarDays(end, start) + 1
  const pctElapsed = Math.min(100, Math.round((daysElapsed / totalDays) * 100))

  // Cumulative chart series. Recognized is null past today (so recharts
  // ends the line cleanly); projection is null before today (line starts
  // at today's recognized-to-date). On the boundary day both share the
  // same value so the two lines visually connect.
  const bucketBy = PERIODS.find(p => p.id === id)!.bucketBy
  const stamps = bucketBy === 'day'
    ? days
    : eachMonthOfInterval({ start, end })
  const labelFmt = bucketBy === 'day' ? 'MMM d' : 'MMM'
  const isMonthlyView = bucketBy === 'month'

  let cumRecognized = 0
  let cumProjection = 0
  const series = stamps.map(s => {
    const bucketStart = isMonthlyView ? startOfMonth(s) : startOfDay(s)
    const bucketEnd   = isMonthlyView ? endOfMonth(s)   : endOfDay(s)
    // Recognized: sum of completes in this bucket.
    let recognizedInBucket = 0
    for (const j of completeJobs) {
      const dt = completedAt(j); if (!dt) continue
      if (isWithinInterval(dt, { start: bucketStart, end: bucketEnd })) recognizedInBucket += jobValue(j)
    }
    // Projection: for day buckets we already computed scheduledByDay;
    // for month buckets we sum scheduled across the month. Both apply
    // the max(pace, scheduled) rule.
    let scheduledInBucket = 0
    if (isMonthlyView) {
      for (const j of scheduledJobs) {
        const dt = scheduledAt(j); if (!dt) continue
        if (isWithinInterval(dt, { start: bucketStart, end: bucketEnd })) scheduledInBucket += jobValue(j)
      }
    } else {
      scheduledInBucket = scheduledByDay.get(format(s, 'yyyy-MM-dd')) ?? 0
    }

    const isPast   = bucketEnd < today
    const isToday  = bucketStart <= today && today <= bucketEnd
    const isFuture = bucketStart > today

    if (isPast || isToday) cumRecognized += recognizedInBucket
    // Projection cumulative tracks recognized through today, then adds
    // max(pace, scheduled) per bucket for the remainder. For the monthly
    // view we apply pace per BUCKET (month) — pace × (days remaining in
    // that month) — so the projected month total reflects multiple days.
    if (isFuture) {
      const daysIn = isMonthlyView ? differenceInCalendarDays(bucketEnd, bucketStart) + 1 : 1
      const paced = dailyPace * daysIn
      cumProjection += Math.max(paced, scheduledInBucket)
    } else if (isToday) {
      // Today is the seam — projection picks up exactly where recognized
      // lands. Don't add anything beyond the recognized-to-date.
      cumProjection = cumRecognized
    } else {
      cumProjection = cumRecognized
    }

    return {
      label: format(s, labelFmt),
      ts: s.getTime(),
      // null past today on projection / future on recognized so the
      // dashed line only spans its own segment and the solid Area cleanly
      // ends at today.
      recognized: (isPast || isToday) ? cumRecognized : null,
      projection: (isToday || isFuture) ? cumProjection : null,
      todayMark: isToday ? cumRecognized : null,
    }
  })

  return {
    recognizedToDate,
    projection,
    pctElapsed,
    series,
    todayLabel: format(today, labelFmt),
    isEmpty: recognizedToDate === 0 && scheduledByDay.size > 0 && Array.from(scheduledByDay.values()).every(v => v === 0),
  }
}

function ProjectionsTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null
  const rec = payload.find((p: any) => p.dataKey === 'recognized')?.value
  const proj = payload.find((p: any) => p.dataKey === 'projection')?.value
  return (
    <div style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-default)', borderRadius: 10, padding: '8px 12px', fontSize: 12, color: 'var(--ink-primary)', minWidth: 170 }}>
      <p style={{ fontSize: 11, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>{label}</p>
      {rec != null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Recognized</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--positive)' }}>{formatCurrency(rec)}</span>
        </div>
      )}
      {proj != null && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 2 }}>
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Projected</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{formatCurrency(proj)}</span>
        </div>
      )}
    </div>
  )
}

export default function RevenueProjections({ jobs, isMobile = false }: { jobs: any[]; isMobile?: boolean }) {
  const [selected, setSelected] = useState<PeriodId>(() => {
    try {
      const raw = localStorage.getItem(PERIOD_KEY)
      const valid = PERIODS.some(p => p.id === raw)
      return valid ? (raw as PeriodId) : 'month'
    } catch { return 'month' }
  })
  useEffect(() => {
    try { localStorage.setItem(PERIOD_KEY, selected) } catch { /* ignore */ }
  }, [selected])

  const now = useMemo(() => new Date(), [])
  const allStats = useMemo(
    () => Object.fromEntries(PERIODS.map(p => [p.id, computeStats(p.id, jobs, now)])) as Record<PeriodId, ReturnType<typeof computeStats>>,
    [jobs, now],
  )
  const stats = allStats[selected]

  // Empty-period messaging: zero recognized AND zero scheduled. Render
  // $0 with a muted "no data yet" label rather than letting an empty
  // chart sit blankly.
  const isEmpty = stats.recognizedToDate === 0 && stats.projection === 0

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: isMobile ? '14px 14px 6px' : '18px 20px 10px', marginBottom: 16 }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>
        Revenue projections
      </p>

      {/* Three cards. auto-fit so they reflow on narrow viewports. */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
        {PERIODS.map(p => {
          const s = allStats[p.id]
          const active = selected === p.id
          return (
            <button key={p.id} onClick={() => setSelected(p.id)}
              style={{
                textAlign: 'left',
                background: active ? 'var(--accent-dim)' : 'var(--hover-bg)',
                border: `1px solid ${active ? 'var(--accent-border)' : 'var(--border-subtle)'}`,
                borderRadius: 12,
                padding: '12px 14px',
                cursor: 'pointer',
                transition: 'background 150ms ease, border-color 150ms ease',
              }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>{p.label}</p>
              <p style={{ fontSize: isMobile ? 20 : 24, fontWeight: 900, color: 'var(--accent-text)', margin: '4px 0 0', letterSpacing: '-0.02em' }}>
                {formatCurrency(s.projection)}
              </p>
              <p style={{ fontSize: 11, color: 'var(--ink-secondary)', margin: '4px 0 0' }}>
                {formatCurrency(s.recognizedToDate)} recognized · <span style={{ color: 'var(--ink-tertiary)' }}>{s.pctElapsed}% elapsed</span>
              </p>
            </button>
          )
        })}
      </div>

      {/* Selected-card chart. Cumulative recognized (Area, --positive)
          through today; cumulative projection (dotted Line, --accent)
          from today onward. ReferenceLine at today separates the two. */}
      {isEmpty ? (
        <div style={{ padding: '40px 12px', textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>No revenue data for this period yet.</p>
          <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '4px 0 0' }}>Schedule or complete a job to start the projection.</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6, fontSize: 11, color: 'var(--ink-tertiary)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span aria-hidden style={{ width: 14, height: 2, background: 'var(--positive)', borderRadius: 2 }} />
              Recognized (cumulative)
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span aria-hidden style={{ width: 14, height: 0, borderTop: '1.5px dotted var(--accent)' }} />
              Projection
            </span>
          </div>
          <ResponsiveContainer width="100%" height={isMobile ? 140 : 180}>
            <ComposedChart data={stats.series} margin={{ top: 0, right: 4, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="projRecGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="var(--positive)" stopOpacity={0.22} />
                  <stop offset="95%" stopColor="var(--positive)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--hover-bg)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: 'var(--ink-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} minTickGap={isMobile ? 20 : 30} />
              <YAxis tick={{ fill: 'var(--ink-tertiary)', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} width={48} />
              <Tooltip content={<ProjectionsTooltip />} />
              {/* "Today" reference — a dimmed vertical guide where the
                  recognized line hands off to the projection. */}
              <ReferenceLine x={stats.todayLabel} stroke="var(--border-default)" strokeDasharray="2 4" />
              <Area type="monotone" dataKey="recognized" stroke="var(--positive)" strokeWidth={2} fill="url(#projRecGrad)" connectNulls={false} />
              <Line type="monotone" dataKey="projection" stroke="var(--accent)" strokeWidth={1.75} strokeDasharray="2 3" dot={false} connectNulls={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  )
}
